/**
 * 장소 선택 모달 — PlaceMapView (카카오/Leaflet 검색 + 추천 + 즐겨찾기) 를
 * full-screen 모달 wrapper 로 감싼 재사용 컴포넌트.
 *
 * 사용처:
 *  - 모임 sub_place 추가 (SubPlaceModal 안에 임베드)
 *  - 급번개 후보 제안 (FlashVote)
 *  - 그 외 "장소 한 개 골라" 가 필요한 모든 흐름
 *
 * onSelect 가 호출되면 모달은 자동 닫힘 (호출자가 close 안 시켜도 됨).
 */
import React, { useEffect, useRef, useState } from 'react';
import i18n from 'i18next';
import { PlaceMapView, MapPlace } from './PlaceMapView';
import { IcClose } from 'components/common/icons/EmojiIcons';
import { isInKorea } from 'common/utils/regionDetector';
import { isNativeApp, openNativePlaceExplore } from 'common/webview';
import config from 'config/config';

interface Props {
    open: boolean;
    onClose: () => void;
    onSelect: (place: MapPlace) => void;
    /** 모달 헤더 타이틀 (기본: "장소 선택") */
    title?: string;
    /** PlaceMapView 의 [이 장소로] 같은 confirm 버튼 라벨 */
    confirmButtonText?: string;
    /** 초기에 외부에서 fetch 해 넘기는 추천/즐겨찾기 장소 (없으면 빈 배열) */
    initialPlaces?: MapPlace[];
    /** 지도 초기 중심 좌표 (없으면 PlaceMapView 의 default) */
    initialCenter?: { lat: number; lng: number };
}

export const PlacePickerModal: React.FC<Props> = ({
    open,
    onClose,
    onSelect,
    title = i18n.t('placeMap.pickTitle'),
    confirmButtonText = i18n.t('placeMap.pickConfirm'),
    initialPlaces = [],
    initialCenter,
}) => {
    const [places] = useState<MapPlace[]>(initialPlaces);
    const [selectedPlace, setSelectedPlace] = useState<MapPlace | null>(null);
    // 네이티브 탐색 화면을 두 번 열지 않기 위한 표시
    const nativeOpenedRef = useRef(false);
    const onSelectRef = useRef(onSelect);
    onSelectRef.current = onSelect;
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    /* 해외 + 네이티브 → 네이티브 탐색 화면으로 넘깁니다.
       웹뷰 안에서 해외 지도를 직접 그리지 않기 위한 것입니다 —
       다른 장소 고르기 화면(PlaceSearch·PlaceExplore 등)도 모두 같은 방식입니다. */
    useEffect(() => {
        if (!open) { nativeOpenedRef.current = false; return; }
        if (!initialCenter || !isNativeApp()) return;
        if (isInKorea(initialCenter.lat, initialCenter.lng)) return;
        if (nativeOpenedRef.current) return;
        nativeOpenedRef.current = true;
        openNativePlaceExplore({
            places: initialPlaces.filter(p => p.lat && p.lng).map(p => ({
                id: p.id, name: p.name, address: p.address || '', lat: p.lat!, lng: p.lng!,
                imageUrl: p.imageUrl, rating: p.rating, categoryLabel: p.categoryLabel,
            })),
            center: initialCenter,
            confirmButtonText,
            // 이 모달을 부르는 쪽이 준 제목을 네이티브 화면에도 그대로 씁니다
            title,
            apiUrl: config.API_URL,
            mode: 'general',
        }).then((result) => {
            if (result) onSelectRef.current(result as unknown as MapPlace);
            onCloseRef.current();
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialCenter?.lat, initialCenter?.lng]);

    if (!open) return null;

    // 해외 + 네이티브면 위 이펙트가 네이티브 화면을 띄웁니다 — 웹 지도는 그리지 않습니다.
    if (initialCenter && isNativeApp() && !isInKorea(initialCenter.lat, initialCenter.lng)) return null;

    return (
        <div
            role="dialog"
            aria-modal="true"
            onClick={(e) => { e.stopPropagation(); }}
            style={{
                position: 'fixed', inset: 0, background: '#fff',
                zIndex: 2000, display: 'flex', flexDirection: 'column',
            }}
        >
            {/* 헤더 */}
            <div style={{
                padding: '14px 16px',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                flex: '0 0 auto',
            }}>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onClose(); }}
                    aria-label="뒤로"
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, fontSize: 18, color: '#666' }}
                >
                    ‹
                </button>
                <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, flex: 1, textAlign: 'center' }}>
                    {title}
                </h2>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onClose(); }}
                    aria-label="닫기"
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, fontSize: 20, color: '#666' }}
                >
                    <IcClose size={20} />
                </button>
            </div>

            {/* PlaceMapView 본체 */}
            <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
                <PlaceMapView
                    places={places}
                    center={initialCenter}
                    selectedPlaceId={selectedPlace?.id}
                    onPlaceSelect={(p) => setSelectedPlace(p)}
                    onPlaceConfirm={(p) => {
                        onSelect(p);
                        onClose();
                    }}
                    showSearch
                    confirmButtonText={confirmButtonText}
                    height="100%"
                />
            </div>
        </div>
    );
};
